import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { css } from '@emotion/core';
import { IconButton, Tooltip, Badge } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import InvalidIcon from '@material-ui/icons/Warning';
import { Observer } from 'mobx-react';
import { getColorByState } from '../../../../stores/dealMapperStore/dealMapUtil';
import RenderPropModal from '../../common/RenderPropModal';
import { useRootStore } from '../../../../stores/BridgeProvider';
import { safeEval } from '../../../util/UtilFunctions';
import { humanReadableIdManager } from '../../../../stores/dealMapperStore/HumanReadableIdManager';
import IdLabel from '../../common/IdLabel';
import Cell from '../../common/Cell';
import DealTransactionModal from './DealTransactionModal';
import { COLORS } from '../../../util/Colors';
import { DealTransactionSchema } from '../../../../validation';
import { useSnackBars } from '../../../../alerts/SnackBarProvider';

const infoStyle = css`
  display: flex;
  justify-content: space-between;
  padding: 0px 4px 0px 10px;
  align-items: center;
  height: 60px;
  font-size: 0.8rem;
  text-align: left;
  span {
    flex: 1;
  }
  h3 {
    flex: 1;
    margin-bottom: 2px;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: clip;
  }
  .tranType {
    max-width: 25px;
    margin-right: 1ch;
  }
  .tranCode {
    max-width: 6ch;
    width: 6ch;
    margin-right: 1ch;
  }
  .amount {
    max-width: 9ch;
    width: 9ch;
    margin-left: 1ch;
    text-align: right;
  }
`;

const editButtonStyle = css`
  color: inherit !important;
  margin-left: 5px !important;
  svg {
    height: 0.8em;
    width: 0.8em;
  }
`;

const TranTypeStyle = css`
  position: absolute !important;
  margin: 0px;
  font-size: 0.9rem !important;
  left: 9px;
  top: 9px;
`;

// TODO
const DealTransaction = ({ original }) => (
  <Observer
    render={() => {
      const { dataStore, dealMapperStore } = useRootStore();
      const [validationErrors, setValidationErrors] = useState(null);
      const {
        getContactFromId,
        getTranCategoryFromId,
        getAccountFromId,
      } = dataStore;
      const color = getColorByState(original);
      const ref = useRef(null);
      useLayoutEffect(() => {
        dealMapperStore.dealMapperViewStore.pushDealTransactionRef(
          ref,
          original.DealTranId,
          original.sectionId,
        );
        return () => {
          dealMapperStore.dealMapperViewStore.removeDealTransactionRef(
            original.DealTranId,
            original.sectionId,
          );
        };
      }, [ref]);
      const handleAlert = useSnackBars();
      const {
        DealTranId,
        ContactId,
        Amount,
        Quantity,
        Price,
        TranType,
        CategoryId,
        DealId,
        DebitAccount,
        CreditAccount,
      } = original;

      // FIXME: Price - if no amount then we'll need price * quantity and have to check rounding

      const calculatedAmount = Quantity * Price;

      useEffect(() => {
        let unmounted = false;
        const checkValidity = async () => {
          try {
            await DealTransactionSchema.validate(original, {
              abortEarly: false,
            });
            if (validationErrors) setValidationErrors(null);
          } catch (e) {
            if (!unmounted) {
              setValidationErrors(e.errors);
            }
          }
        };

        checkValidity();
        return () => {
          unmounted = true;
        };
      }, [original]);

      const contactName = safeEval(
        () => getContactFromId(ContactId).Name,
        'N/A',
      );
      const tranCategoryCode = safeEval(
        () => getTranCategoryFromId(CategoryId).Code,
        'N/A',
      );

      const selected = safeEval(
        () => dealMapperStore.selectedDealTransaction.DealTranId === DealTranId,
        false,
      );

      const SelectTransaction = e => {
        e.stopPropagation();
        const returnVal = dealMapperStore.setSelectedDealTransaction(
          selected ? null : original,
        );
        if (returnVal === 'INCOMPATIBLE') {
          handleAlert(
            'Linking transations of type JE, or or type AR to AP is not allowed',
            COLORS.red,
          );
        }
      };

      return (
        <RenderPropModal
          cardDisplay={({ handleClose }) => (
            <DealTransactionModal
              dealTransactionId={original.DealTranId}
              handleClose={handleClose}
            />
          )}
          enterHover={() => {
            dealMapperStore.dealMapperViewStore.setHoveredElement(original);
          }}
          exitHover={() => {
            dealMapperStore.dealMapperViewStore.setHoveredElement(null);
          }}
        >
          {({ handleOpen }) => (
            <Observer
              render={() => {
                const isHovered = safeEval(
                  () =>
                    dealMapperStore.dealMapperViewStore.highlightedDealTransactions.some(
                      dT => dT.DealTranId === original.DealTranId,
                    ),
                  false,
                );

                return (
                  <div
                    onClick={SelectTransaction}
                    ref={ref}
                    style={{
                      // width: '90%',
                      width: '100%',
                      margin: '0 auto',
                      // marginBottom: '1rem',
                    }}
                  >
                    <Cell
                      cellObject={original}
                      subCell
                      style={{
                        ...(isHovered && {
                          boxShadow: `0px 1px 3px 0px rgba(0, 0, 0, 0.2),
                            0px 1px 1px 0px rgba(0, 0, 0, 0.14),
                            0px 2px 1px -1px rgba(0, 0, 0, 0.12),
                            inset 0px 0px 400px 110px rgba(0, 0, 0, .1)`,
                        }),
                        ...(selected && {
                          background: COLORS.midnight,
                          color: 'white',
                        }),
                      }}
                    >
                      <div css={infoStyle} ref={ref}>
                        <h3 className="tranType">{TranType}</h3>
                        <h3 className="tranCode">{tranCategoryCode}</h3>
                        <h3>{contactName}</h3>
                        <h3 className="amount">
                          ${parseFloat(calculatedAmount).toFixed(2)}
                        </h3>

                        <IconButton onClick={handleOpen} css={editButtonStyle}>
                          {validationErrors ? (
                            <Tooltip
                              title={
                                <ul
                                  style={{
                                    padding: 0,
                                    listStyleType: 'none',
                                  }}
                                >
                                  {validationErrors.map(error => (
                                    <li key={error}>* {error}</li>
                                  ))}
                                </ul>
                              }
                            >
                              <Badge
                                overlap="circle"
                                badgeContent={
                                  <InvalidIcon
                                    style={{
                                      color: COLORS.red,
                                    }}
                                  />
                                }
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'right',
                                }}
                              >
                                <EditIcon></EditIcon>
                              </Badge>
                            </Tooltip>
                          ) : (
                            <EditIcon />
                          )}
                        </IconButton>
                      </div>
                    </Cell>
                  </div>
                );
              }}
            />
          )}
        </RenderPropModal>
      );
    }}
  />
);

export default DealTransaction;

//  <InvalidIcon
//    style={{
//      color: COLORS.red,
//    }}
//  />;
